// src/components/Knowledge.js
import React, { useEffect, useState } from 'react';
import { fetchAllKnowledge, deleteKnowledge } from '../api/knowledgeApi';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    CircularProgress,
    Alert,
    Box
} from '@mui/material';
import KnowledgeCard from './KnowledgeCard';
import ErrorBoundary from './ErrorBoundary';
import AddKnowledgeModal from './AddKnowledgeModal';

const Knowledge = () => {
    const [knowledge, setKnowledge] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchAllKnowledge();
            setKnowledge(data);
        } catch (err) {
            // Only set error if it's not a "Knowledge Not Found" case
            if (err.response?.status !== 400 || err.response?.data !== 'Knowledge Not Found') {
                setError('Failed to fetch knowledge');
                console.error('Error:', err);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteKnowledge(id);
            setKnowledge((prevData) => prevData.filter((item) => item.id !== id));
        } catch (err) {
            console.error(`Failed to delete knowledge with ID ${id}:`, err);
            setError(`Failed to delete knowledge with ID ${id}`);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleModalClose = () => {
        setIsModalOpen(false);
    };

    const handleSuccess = () => {
        fetchData();
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" component="h1">
                    Knowledge Base
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setIsModalOpen(true)}
                >
                    Add Knowledge
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {knowledge.length === 0 ? (
                <Card sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
                    <CardContent>
                        <Box textAlign="center" py={4}>
                            <Typography variant="h5" color="textSecondary" gutterBottom>
                                No Knowledge Entries Found
                            </Typography>
                            <Typography variant="body1" color="textSecondary" paragraph>
                                Your knowledge base is currently empty. Click the "Add Knowledge" button to create your first entry.
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setIsModalOpen(true)}
                                sx={{ mt: 2 }}
                            >
                                Add Your First Entry
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <Grid container spacing={3}>
                    {knowledge.map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item.id}>
                            <ErrorBoundary>
                                <KnowledgeCard
                                    title={item.knowledgeText}
                                    summary={item.knowledgeSummary}
                                    creationDate={item.creationDate}
                                    user={item.userIdentifier}
                                    userName={item.userName}
                                    id={item.id}
                                    handleDelete={handleDelete}
                                />
                            </ErrorBoundary>
                        </Grid>
                    ))}
                </Grid>
            )}

            <AddKnowledgeModal
                open={isModalOpen}
                onClose={handleModalClose}
                onSuccess={handleSuccess}
            />
        </Container>
    );
};

export default Knowledge;
